import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Documents from './pages/Documents';
import DocumentDetail from './pages/DocumentDetail';
import ReviewCycles from './pages/ReviewCycles';
import Users from './pages/Users';
import ActivityLogs from './pages/ActivityLogs';
import Settings from './pages/Settings';
import Correspondence from './pages/Correspondence';
import TechnicalQueries from './pages/TechnicalQueries';
import Transmittals from './pages/Transmittals';
import DDMSettings from './pages/DDMSettings';
import PublicPortal from './pages/PublicPortal';
import Projects from './pages/Projects';
import BulkUpload from './pages/BulkUpload';
import MasterRegister from './pages/MasterRegister';
import Trash from './pages/Trash';
import Layout from './components/Layout';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios interceptor for auth and cache control
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Add cache-busting headers to prevent stale data
    config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
    config.headers['Pragma'] = 'no-cache';
    config.headers['Expires'] = '0';
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Check if this is a portal request - don't interfere with portal auth
      const isPortalRequest = error.config?.url?.includes('/portal/');
      
      if (!isPortalRequest) {
        // Token expired or invalid for main EDMS - clear auth and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
      // For portal requests, let the portal handle its own 401 errors
    }
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState('');
  const [projects, setProjects] = useState([]);
  const [forceChangeOpen, setForceChangeOpen] = useState(false);
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');

  // Fetch projects for the global selector
  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data || []);
    } catch (error) {
      console.error('Failed to fetch projects');
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      fetchProjects(); // Fetch projects when user is logged in
    }
    setLoading(false);
  }, []);

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    fetchProjects();
    if (userData.force_password_change) {
      setForceChangeOpen(true);
    }
  };

  const handleForcePasswordChange = async () => {
    if (!newPwd || newPwd !== confirmPwd) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPwd.length < 8 || !/[A-Z]/.test(newPwd) || !/[a-z]/.test(newPwd) || !/[0-9]/.test(newPwd) || !/[!@#$%^&*()_+\-=\[\]{}|;:',.<>?/~`]/.test(newPwd)) {
      toast.error('Password must be 8+ chars with uppercase, lowercase, number, and special character');
      return;
    }
    try {
      await axios.post(`${API}/auth/change-password`, { new_password: newPwd });
      toast.success('Password changed successfully');
      setForceChangeOpen(false);
      setNewPwd('');
      setConfirmPwd('');
      const updatedUser = { ...user, force_password_change: false };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setSelectedProject('');
    setProjects([]);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Public Portal - No Auth Required */}
          <Route path="/public" element={<PublicPortal />} />
          <Route path="/portal" element={<PublicPortal />} />
          
          <Route
            path="/login"
            element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/" />}
          />
          <Route
            path="/"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout} selectedProject={selectedProject} setSelectedProject={setSelectedProject} projects={projects}>
                  <Dashboard user={user} selectedProject={selectedProject} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/transmittals"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout} selectedProject={selectedProject} setSelectedProject={setSelectedProject} projects={projects}>
                  <Transmittals user={user} selectedProject={selectedProject} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/documents"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout} selectedProject={selectedProject} setSelectedProject={setSelectedProject} projects={projects}>
                  <Documents user={user} selectedProject={selectedProject} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/documents/:id"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout} selectedProject={selectedProject} setSelectedProject={setSelectedProject} projects={projects}>
                  <DocumentDetail user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/documents/:documentId/cycles"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout} selectedProject={selectedProject} setSelectedProject={setSelectedProject} projects={projects}>
                  <ReviewCycles user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />

          <Route
            path="/ddm"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout} selectedProject={selectedProject} setSelectedProject={setSelectedProject} projects={projects}>
                  <DDMSettings user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/users"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout} selectedProject={selectedProject} setSelectedProject={setSelectedProject} projects={projects}>
                  <Users user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/activity"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout} selectedProject={selectedProject} setSelectedProject={setSelectedProject} projects={projects}>
                  <ActivityLogs user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/settings"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout} selectedProject={selectedProject} setSelectedProject={setSelectedProject} projects={projects}>
                  <Settings user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/correspondence"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout} selectedProject={selectedProject} setSelectedProject={setSelectedProject} projects={projects}>
                  <Correspondence user={user} selectedProject={selectedProject} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/queries"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout} selectedProject={selectedProject} setSelectedProject={setSelectedProject} projects={projects}>
                  <TechnicalQueries user={user} selectedProject={selectedProject} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/projects"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout} selectedProject={selectedProject} setSelectedProject={setSelectedProject} projects={projects}>
                  <Projects user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/bulk-upload"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout} selectedProject={selectedProject} setSelectedProject={setSelectedProject} projects={projects}>
                  <BulkUpload user={user} selectedProjectId={selectedProject} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
                  <Route
            path="/trash"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout} selectedProject={selectedProject} setSelectedProject={setSelectedProject} projects={projects}>
                  <Trash user={user} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/mdr"
            element={
              user ? (
                <Layout user={user} onLogout={handleLogout} selectedProject={selectedProject} setSelectedProject={setSelectedProject} projects={projects}>
                  <MasterRegister user={user} selectedProject={selectedProject} setSelectedProject={setSelectedProject} projects={projects} />
                </Layout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
        </Routes>
      </BrowserRouter>
      <Dialog open={forceChangeOpen} onOpenChange={() => {}}>
        <DialogContent className="max-w-md" onInteractOutside={(e) => e.preventDefault()} data-testid="force-password-change-dialog">
          <DialogHeader>
            <DialogTitle>Password Change Required</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Your administrator requires you to change your password before continuing.</p>
            <div>
              <Label>New Password</Label>
              <Input type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} placeholder="Enter new password" data-testid="force-new-password" />
            </div>
            <div>
              <Label>Confirm Password</Label>
              <Input type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} placeholder="Confirm new password" data-testid="force-confirm-password" />
            </div>
            {newPwd && (
              <div className="grid grid-cols-2 gap-1 text-xs">
                <span className={newPwd.length >= 8 ? 'text-green-600' : 'text-red-500'}>{newPwd.length >= 8 ? '\u2713' : '\u2717'} Min 8 characters</span>
                <span className={/[A-Z]/.test(newPwd) ? 'text-green-600' : 'text-red-500'}>{/[A-Z]/.test(newPwd) ? '\u2713' : '\u2717'} Uppercase</span>
                <span className={/[a-z]/.test(newPwd) ? 'text-green-600' : 'text-red-500'}>{/[a-z]/.test(newPwd) ? '\u2713' : '\u2717'} Lowercase</span>
                <span className={/[0-9]/.test(newPwd) ? 'text-green-600' : 'text-red-500'}>{/[0-9]/.test(newPwd) ? '\u2713' : '\u2717'} Number</span>
                <span className={/[!@#$%^&*()_+\-=\[\]{}|;:',.<>?\/~`]/.test(newPwd) ? 'text-green-600' : 'text-red-500'}>{/[!@#$%^&*()_+\-=\[\]{}|;:',.<>?\/~`]/.test(newPwd) ? '\u2713' : '\u2717'} Special char</span>
                <span className={newPwd && confirmPwd && newPwd === confirmPwd ? 'text-green-600' : 'text-red-500'}>{newPwd && confirmPwd && newPwd === confirmPwd ? '\u2713' : '\u2717'} Passwords match</span>
              </div>
            )}
            <Button onClick={handleForcePasswordChange} className="w-full" data-testid="force-change-submit">Change Password</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Toaster />
    </div>
  );
}

export default App;
